import { z } from 'zod';
import { ScaffoldConfig, Question } from './types';

export class ValidationEngine {
  /**
   * Validate scaffold configuration against schema
   */
  static validateConfig(config: ScaffoldConfig, schema: z.ZodSchema): { success: boolean; errors: string[] } {
    try {
      schema.parse(config);
      return { success: true, errors: [] };
    } catch (error) {
      if (error instanceof z.ZodError) {
        return {
          success: false,
          errors: error.issues.map((err: any) => `${err.path.join('.')}: ${err.message}`)
        };
      }
      return {
        success: false,
        errors: ['Unknown validation error']
      };
    }
  }

  /**
   * Validate individual question response
   */
  static validateQuestion(value: any, question: Question): boolean | string {
    if (question.validate) {
      return question.validate(value);
    }

    // Basic validation based on question type
    switch (question.type) {
      case 'text':
        return typeof value === 'string' && value.length > 0;
      case 'select':
        return question.choices?.includes(value) || false;
      case 'confirm':
        return typeof value === 'boolean';
      case 'multiselect':
        return Array.isArray(value) && value.length > 0;
      default:
        return true;
    }
  }

  /**
   * Common validation schemas
   */
  static schemas = {
    env: z.object({
      useZod: z.boolean(),
      includeExamples: z.boolean(),
      framework: z.enum(['nextjs', 'react', 'express', 'node'])
    }),
    
    prettier: z.object({
      semicolons: z.boolean(),
      singleQuotes: z.boolean(),
      tabWidth: z.number().min(1).max(8),
      includeESLint: z.boolean()
    })
  };
}

export class TemplateEngine {
  /**
   * Simple template variable replacement
   */
  static renderTemplate(template: string, variables: Record<string, any>): string {
    return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return variables[key] !== undefined ? String(variables[key]) : match;
    });
  }

  /**
   * Render template with conditional blocks
   */
  static renderTemplateWithConditionals(template: string, variables: Record<string, any>): string {
    // Handle {{#if condition}}...{{/if}} blocks
    let result = template.replace(/\{\{#if\s+(\w+)\}\}([\s\S]*?)\{\{\/if\}\}/g, (_, condition, content) => {
      return variables[condition] ? content : '';
    });

    // Handle {{#unless condition}}...{{/unless}} blocks
    result = result.replace(/\{\{#unless\s+(\w+)\}\}([\s\S]*?)\{\{\/unless\}\}/g, (_, condition, content) => {
      return !variables[condition] ? content : '';
    });

    // Handle simple variable replacement
    return this.renderTemplate(result, variables);
  }

  /**
   * Generate file content from template
   */
  static generateFileContent(templatePath: string, variables: Record<string, any>): string {
    // In a real implementation, this would read from template files
    // For now, we'll use inline templates
    const templates: Record<string, string> = {
      'env.example': `# Environment Variables
# Copy this file to .env.local and fill in your values

{{#if includeExamples}}
# Database
DATABASE_URL="postgresql://username:password@localhost:5432/database"

# Authentication
NEXTAUTH_SECRET="your-secret-key"
NEXTAUTH_URL="http://localhost:3000"

# API Keys
STRIPE_SECRET_KEY="sk_test_..."
OPENAI_API_KEY="sk-..."
{{/if}}
`,

      'env.mjs': `import { createEnv } from "@t3-oss/env-{{framework}}";
import { z } from "zod";

export const env = createEnv({
  server: {
    {{#if includeExamples}}
    DATABASE_URL: z.string().url(),
    NEXTAUTH_SECRET: z.string().min(1),
    NEXTAUTH_URL: z.string().url(),
    {{/if}}
  },
  client: {
    // Add client-side env vars here
  },
  runtimeEnv: {
    {{#if includeExamples}}
    DATABASE_URL: process.env.DATABASE_URL,
    NEXTAUTH_SECRET: process.env.NEXTAUTH_SECRET,
    NEXTAUTH_URL: process.env.NEXTAUTH_URL,
    {{/if}}
  },
});
`,

      'prettierrc.json': `{
  "semi": {{semicolons}},
  "singleQuote": {{singleQuotes}},
  "tabWidth": {{tabWidth}},
  "trailingComma": "es5",
  "printWidth": 80,
  "endOfLine": "lf"
}
`,

      'prettierignore': `node_modules/
.next/
dist/
build/
*.log
.env*
`
    };

    const template = templates[templatePath];
    if (!template) {
      throw new Error(`Template not found: ${templatePath}`);
    }

    return this.renderTemplateWithConditionals(template, variables);
  }
}


